import React, { useState } from 'react';
import {
  Box,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Stack,
  Typography,
  Paper,
  IconButton,
  Collapse,
  Button,
  Autocomplete,
  TextField
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { Venue } from '../types/mongodb';
import type { Strand } from '../types/Strand';

interface PerformanceFiltersProps {
  events: string[];
  bands: string[];
  strands: Strand[];
  venues: Venue[];
  filters: {
    event: string;
    band: string;
    strand: string;
    venue: string;
    status: string;
    timeframe: 'all' | 'past' | 'upcoming' | 'thisMonth' | 'thisYear';
  };
  onFilterChange: (filterName: string, value: any) => void;
  activeFilters: string[];
  onClearFilter: (filterName: string) => void;
}

const CollapsiblePerformanceFilters: React.FC<PerformanceFiltersProps> = ({
  events,
  bands,
  strands,
  venues,
  filters,
  onFilterChange,
  activeFilters,
  onClearFilter
}) => {
  const [expanded, setExpanded] = useState(false);

  const handleToggleExpand = () => {
    setExpanded(prev => !prev);
  };

  const mapFilterKey = (label: string) => {
    const keyRaw = label.split(':')[0].trim().toLowerCase();
    // map timeframe labels back to 'timeframe'
    if (['all', 'past', 'upcoming', 'thismonth', 'thisyear'].includes(keyRaw)) {
      return 'timeframe';
    }
    return keyRaw;
  };

  const handleClearAll = () => {
    activeFilters.forEach(f => {
      const key = mapFilterKey(f);
      onClearFilter(key);
    });
  };

  const handleBandChange = (_: any, val: string | null) => {
    // clear any timeframe filter so band search isn't restricted unintentionally
    if (filters.timeframe !== 'all') {
      onFilterChange('timeframe', 'all');
    }
    onFilterChange('band', val || '');
  };

  return (
    <Paper sx={{ mb: 2, overflow: 'hidden' }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: 1,
          bgcolor: 'background.paper',
          borderBottom: expanded ? 1 : 0,
          borderColor: 'divider'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FilterListIcon />
          <Typography variant="subtitle1">
            Filters {activeFilters.length > 0 && `(${activeFilters.length})`}
          </Typography>
          {/* Quick Past filter button */}
          <Button
            size="small"
            onClick={() => onFilterChange('timeframe', 'past')}
          >
            Past
          </Button>
          <IconButton onClick={handleToggleExpand} size="small">
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>

        {activeFilters.length > 0 && (
          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
            {activeFilters.map(f => (
              <Chip
                key={f}
                label={f}
                onDelete={() => onClearFilter(mapFilterKey(f))}
                size="small"
              />
            ))}
            <Button size="small" onClick={handleClearAll}>
              Clear All
            </Button>
          </Stack>
        )}
      </Box>

      <Collapse in={expanded} unmountOnExit>
        <Box sx={{ p: 1 }}>
          <Grid container spacing={1}>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Timeframe</InputLabel>
                <Select
                  value={filters.timeframe}
                  onChange={e => onFilterChange('timeframe', e.target.value)}
                  label="Timeframe"
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="upcoming">Upcoming</MenuItem>
                  <MenuItem value="past">Past</MenuItem>
                  <MenuItem value="thisMonth">This Month</MenuItem>
                  <MenuItem value="thisYear">This Year</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={3}>
              <Autocomplete
                options={bands}
                value={filters.band}
                onChange={handleBandChange}
                freeSolo
                size="small"
                renderInput={params => (
                  <TextField {...params} label="Band" fullWidth size="small" />
                )}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Event</InputLabel>
                <Select
                  value={filters.event}
                  onChange={e => onFilterChange('event', e.target.value)}
                  label="Event"
                >
                  <MenuItem value="">All</MenuItem>
                  {events.map(evt => (
                    <MenuItem key={evt} value={evt}>{evt}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Strand</InputLabel>
                <Select
                  value={filters.strand}
                  onChange={e => onFilterChange('strand', e.target.value)}
                  label="Strand"
                >
                  <MenuItem value="">All</MenuItem>
                  {strands.map(s => (
                    <MenuItem key={s._id} value={s._id}>{s.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Venue</InputLabel>
                <Select
                  value={filters.venue}
                  onChange={e => onFilterChange('venue', e.target.value)}
                  label="Venue"
                >
                  <MenuItem value="">All</MenuItem>
                  {venues.map(v => (
                    <MenuItem key={v._id} value={v._id}>{v.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={filters.status}
                  onChange={e => onFilterChange('status', e.target.value)}
                  label="Status"
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="confirmed">Confirmed</MenuItem>
                  <MenuItem value="cancelled">Cancelled</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Box>
      </Collapse>
    </Paper>
  );
};

export default CollapsiblePerformanceFilters;
